import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, FileText, PaperPlaneTilt, Files } from "@phosphor-icons/react";

const statusBadge = (status) => {
  const map = {
    draft: "bg-amber-50 text-amber-700 border-amber-200",
    ready: "bg-blue-50 text-blue-700 border-blue-300",
    submitted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return `text-xs px-2 py-0.5 border font-mono uppercase tracking-wider ${map[status] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`;
};

const effectiveStatus = (p) =>
  p.status === "draft" && (p.placeholders_left ?? 0) === 0 ? "ready" : p.status;

function MetricCard({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="gf-sharp border border-zinc-200 bg-white p-6 flex items-start justify-between">
      <div>
        <div className="gf-overline">{label}</div>
        <div className="font-display font-black text-4xl mt-3 tracking-tight tabular-nums">{value}</div>
      </div>
      <Icon size={28} weight="regular" className="text-zinc-400" />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, drafts: 0, submitted: 0, emailed: 0 });
  const [proposals, setProposals] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/proposals/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/proposals").then((r) => setProposals(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const onQuickSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="max-w-7xl">
      <div className="gf-overline">Dashboard</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2" data-testid="dashboard-greeting">
        Welcome, {user?.name?.split(" ")[0] || "there"}.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
        Monitor your proposal pipeline and jump into a new federal grant search.
      </p>

      <form onSubmit={onQuickSearch} className="mt-8 flex gap-2 max-w-2xl" data-testid="dashboard-quick-search">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search grants by topic (e.g., 'rural broadband')"
            className="gf-sharp pl-10 h-12 border-zinc-300 bg-white text-base"
            data-testid="dashboard-search-input"
          />
        </div>
        <Button type="submit" className="gf-sharp h-12 px-6 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide" data-testid="dashboard-search-btn">
          Search
        </Button>
      </form>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-10">
        <MetricCard label="Total Proposals" value={stats.total} icon={Files} testid="metric-total" />
        <MetricCard label="Drafts" value={stats.drafts} icon={FileText} testid="metric-drafts" />
        <MetricCard label="Submitted" value={stats.submitted} icon={PaperPlaneTilt} testid="metric-submitted" />
        <MetricCard label="Emailed" value={stats.emailed} icon={PaperPlaneTilt} testid="metric-emailed" />
      </div>

      <div className="mt-12 border border-zinc-200 bg-white" data-testid="recent-proposals">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <div className="gf-overline">Recent Proposals</div>
            <div className="font-display font-bold text-xl mt-1">Your latest drafts &amp; submissions</div>
          </div>
          <Link to="/proposals" className="text-sm font-semibold text-blue-700 hover:underline uppercase tracking-wide" data-testid="link-all-proposals">
            View all →
          </Link>
        </div>
        {proposals.length === 0 ? (
          <div className="p-12 text-center">
            <div className="gf-overline">No proposals yet</div>
            <p className="text-zinc-600 mt-2 text-sm">
              Start by searching for a grant and generating your first draft.
            </p>
            <Link to="/search">
              <Button className="gf-sharp mt-5 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide" data-testid="cta-find-grants">
                Find grants
              </Button>
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-zinc-200 text-left gf-overline">
                <th className="px-6 py-3">Title</th>
                <th className="px-6 py-3">Agency</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Updated</th>
                <th className="px-6 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {proposals.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-6 py-4 font-semibold max-w-sm truncate">{p.title}</td>
                  <td className="px-6 py-4 text-zinc-600 font-mono text-xs">{p.grant_agency}</td>
                  <td className="px-6 py-4"><span className={statusBadge(effectiveStatus(p))}>{effectiveStatus(p)}</span></td>
                  <td className="px-6 py-4 text-zinc-500 font-mono text-xs">
                    {new Date(p.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link to={`/proposals/${p.id}`} className="text-blue-700 font-semibold hover:underline" data-testid={`open-proposal-${p.id}`}>
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
